import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FileText, Clock, User, Package, Filter, ChevronLeft, ChevronRight,
  Shield, RefreshCcw, Mail, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    loadLogs();
  }, [page, actionFilter]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/admin-audit-log?page=${page}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs || []);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const badges = {
      'creator_suspended': { label: 'Suspended Creator', color: 'bg-red-500/20 text-red-400', icon: Shield },
      'creator_unsuspended': { label: 'Unsuspended Creator', color: 'bg-green-500/20 text-green-400', icon: Shield },
      'product_disabled': { label: 'Disabled Product', color: 'bg-orange-500/20 text-orange-400', icon: Package },
      'product_enabled': { label: 'Enabled Product', color: 'bg-green-500/20 text-green-400', icon: Package },
      'product_approved': { label: 'Approved Product', color: 'bg-green-500/20 text-green-400', icon: Package },
      'product_rejected': { label: 'Rejected Product', color: 'bg-red-500/20 text-red-400', icon: Package },
      'dispute_resolved': { label: 'Resolved Dispute', color: 'bg-blue-500/20 text-blue-400', icon: Shield },
      'email_resent': { label: 'Resent Email', color: 'bg-[#00F0FF]/20 text-[#00F0FF]', icon: Mail },
      'fraud_resolved': { label: 'Resolved Fraud', color: 'bg-yellow-500/20 text-yellow-400', icon: AlertTriangle },
      'refund_issued': { label: 'Issued Refund', color: 'bg-yellow-500/20 text-yellow-400', icon: RefreshCcw },
    };
    return badges[action] || { label: action?.replace(/_/g, ' ') || 'Unknown', color: 'bg-neutral-500/20 text-neutral-400', icon: FileText };
  };

  const getTargetIcon = (targetType) => {
    const icons = {
      'user': User,
      'product': Package,
      'creator': Shield,
      'dispute': AlertTriangle,
      'order': RefreshCcw,
    };
    return icons[targetType] || FileText;
  };

  const actionTypes = [
    'all',
    'creator_suspended',
    'creator_unsuspended',
    'product_disabled',
    'product_enabled',
    'dispute_resolved',
    'email_resent',
    'fraud_resolved',
    'refund_issued'
  ];

  const filteredLogs = actionFilter === 'all' 
    ? logs 
    : logs.filter(l => l.action === actionFilter);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <FileText className="w-8 h-8 text-[#00F0FF]" />
              Audit Logs
            </h1>
            <p className="text-neutral-400 mt-1">Track all admin actions and changes</p>
          </div>
          
          <Button
            onClick={async () => {
              // Spinner appears the moment the click registers and stays
              // visible long enough to feel intentional even when the
              // network is fast — the previous version completed in ~80ms
              // and the user couldn't tell anything had happened.
              await loadLogs();
              toast.success('Logs refreshed');
            }}
            disabled={loading}
            variant="outline"
            className="border-white/20 px-5 py-2 min-w-[120px]"
            data-testid="audit-logs-refresh-btn"
          >
            {loading ? (
              <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCcw className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>

        {/* Action Type Filter */}
        <div className="flex flex-wrap gap-2">
          {actionTypes.map(action => {
            const badge = action === 'all' ? { label: 'All' } : getActionBadge(action);
            return (
              <Button
                key={action}
                size="sm"
                variant={actionFilter === action ? 'default' : 'outline'}
                onClick={() => { setActionFilter(action); setPage(1); }}
                className={actionFilter === action ? 'bg-[#00F0FF] text-black' : 'border-white/20'}
              >
                {badge.label}
              </Button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00F0FF]"></div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12 text-neutral-400">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No audit logs found</p>
          </div>
        ) : (
          <>
            <div className="bg-neutral-900/50 border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-black/30">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Timestamp</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Admin</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Action</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Target</th>
                      <th className="text-left p-4 text-sm font-medium text-neutral-400">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredLogs.map((log, idx) => {
                      const actionBadge = getActionBadge(log.action);
                      const ActionIcon = actionBadge.icon;
                      const TargetIcon = getTargetIcon(log.target_type);
                      // Composite stable key (backend log id may be missing on legacy rows)
                      const rowKey = log.log_id || log._id || `${log.action}-${log.target_id || ''}-${log.created_at || idx}`;
                      return (
                        <tr key={rowKey} className="hover:bg-white/5">
                          <td className="p-4 text-sm text-neutral-400 whitespace-nowrap">
                            {formatDate(log.created_at)}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-neutral-400" />
                              <div>
                                <div className="text-sm">{log.admin_email || log.admin_id}</div>
                                {log.admin_name && (
                                  <div className="text-xs text-neutral-400">{log.admin_name}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${actionBadge.color}`}>
                              <ActionIcon className="w-3 h-3" />
                              {actionBadge.label}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <TargetIcon className="w-4 h-4 text-neutral-400" />
                              <div>
                                <div className="text-xs text-neutral-400 capitalize">{log.target_type}</div>
                                <div className="font-mono text-xs">{log.target_id?.slice(0, 20)}...</div>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-neutral-400 max-w-xs">
                            {log.notes || log.reason || log.resolution || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="border-white/20"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-4 py-2 text-sm">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="border-white/20"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminAuditLogs;
